import { AppAuthContext } from "@/authentication"
import { AbstractHttpService, LoadingError, type RequestOptions } from "vue-utils"
import { ApiUrls } from "./ApiUrls"

export class HttpLoadingError extends LoadingError {
	readonly statusCode: number
	readonly statusText: string

	constructor(response: Response, message: string) {
		super(response.statusText, message)

		this.statusCode = response.status
		this.statusText = response.statusText
	}
}

const statusCodeTexts: Record<number, string> = {
	400: "There was an issue with the data you submitted. Try again or contact an administrator",
	401: "Please refresh your login and try again",
	403: "You do not have sufficient permissions to perform that action",
	404: "A required value was not found. Try again or contact an administrator",
	500: "An internal server error occurred. Try again or contact an administrator",
}

export class BasicHttpService extends AbstractHttpService {
	readonly urlPrefix: string

	constructor(urlPrefix: string) {
		super()

		if (urlPrefix.endsWith("/")) {
			urlPrefix = urlPrefix.substring(0, urlPrefix.length - 1)
		}
		if (!urlPrefix.startsWith("/")) {
			urlPrefix = `/${urlPrefix}`
		}
		this.urlPrefix = urlPrefix
	}

	override async fetch(request: RequestOptions): Promise<Response> {
		let authToken: string | null = null
		try {
			authToken = await AppAuthContext.getBearerToken()
		} catch (e) {
			console.error("Failed to get auth token", e)
		}

		//If the user is not logged in (or getting the token fails), attempt the API call anyway
		//Best case scenario, it doesn't require authentication. Worst case scenario, the user gets an Unauthorized response
		if (authToken) {
			const headers = (request.headers ?? {}) as Record<string, string>
			headers.Authorization = `Bearer ${authToken}`
			request.headers = headers
		}

		const url = this.getUrl(request.url)

		let result
		try {
			result = await fetch(url, request)
		} catch (e) {
			console.error(e)
			throw new LoadingError(
				"Network Error",
				"A network error occurred performing that action. Check your connectivity and try again"
			)
		}
		const statusCode = result.status
		if (result.ok && statusCode >= 200 && statusCode < 300) {
			return result
		} else if (statusCode >= 400 && statusCode < 600) {
			const message = await tryReadJsonMessage(result)
			throw new HttpLoadingError(
				result,
				message ?? statusCodeTexts[statusCode] ?? "An unexpected error occurred performing that action"
			)
		} else {
			throw new HttpLoadingError(result, "An unexpected error occurred performing that action")
		}
	}

	protected getUrl(url: string) {
		if (url.startsWith("http://") || url.startsWith("https://")) {
			return url
		}
		if (!url.startsWith("/")) {
			url = `${this.urlPrefix}/${url}`
			if (!url.startsWith("/")) {
				url = `/${  url}`
			}
		}
		return ApiUrls.NewApi + url
	}
}

export const sendHttpRequest = async (url: string, request?: RequestInit): Promise<Response> =>
	await new BasicHttpService("").fetch({ url, ...request })

const tryReadJsonMessage = async (result: Response): Promise<string | null> => {
	try {
		const json = (await result.json()) as { message?: string }
		return json.message ?? null
	} catch (e) {
		return null
	}
}
